/**
 * Library Management API
 * Thin wrappers over the endpoints the admin modules use:
 *   - Genres        /api/v1/catalog/genres/
 *   - Conditions    /api/v1/inventory/conditions/
 *   - Policies      /api/v1/loans/policies/
 *   - OverdueNotices /api/v1/loans/overdue-notices/
 *   - Reviews       /api/v1/catalog/reviews/  (+ approve / unapprove actions)
 *
 * Field shapes mirror the DRF serializers verified against backend source.
 */
import apiBase, { PaginatedResponse } from './api-base';

/* ───────── Genres ───────── */
export interface Genre {
  id: number;
  name: string;
  description: string;
  parent_genre: number | null;
  parent_genre_name: string | null;
  full_path: string;
  book_count: number;
  subgenres: string[];
  created_at: string;
  updated_at: string;
}
export interface GenreInput {
  name: string;
  description?: string;
  parent_genre?: number | null;
}
export const genresAPI = {
  list: async (search = '', pageSize = 500): Promise<PaginatedResponse<Genre>> => {
    const params: Record<string, string | number> = { page_size: pageSize };
    if (search) params.search = search;
    const { data } = await apiBase.get('/catalog/genres/', { params });
    return data;
  },
  create: async (input: GenreInput): Promise<Genre> => {
    const { data } = await apiBase.post('/catalog/genres/', input);
    return data;
  },
  update: async (id: number, input: GenreInput): Promise<Genre> => {
    const { data } = await apiBase.patch(`/catalog/genres/${id}/`, input);
    return data;
  },
  remove: async (id: number): Promise<void> => {
    await apiBase.delete(`/catalog/genres/${id}/`);
  },
};

/* ───────── Conditions ───────── */
export interface Condition {
  id: number;
  name: string;
  name_display: string;
  description: string;
  score: string;
  can_be_borrowed: boolean;
  requires_special_handling: boolean;
  book_count: number;
  created_at: string;
  updated_at: string;
}
export const CONDITION_OPTIONS = [
  { value: 'excellent', label: 'Excellent - Like new' },
  { value: 'very_good', label: 'Very Good - Minor wear' },
  { value: 'good', label: 'Good - Normal wear' },
  { value: 'fair', label: 'Fair - Noticeable wear' },
  { value: 'poor', label: 'Poor - Significant damage' },
  { value: 'damaged', label: 'Damaged - Requires repair' },
  { value: 'lost', label: 'Lost - Not recoverable' },
] as const;
export interface ConditionInput {
  name: string;
  description: string;
  score: number;
  can_be_borrowed: boolean;
  requires_special_handling: boolean;
}
export const conditionsAPI = {
  list: async (): Promise<PaginatedResponse<Condition>> => {
    const { data } = await apiBase.get('/inventory/conditions/');
    return data;
  },
  create: async (input: ConditionInput): Promise<Condition> => {
    const { data } = await apiBase.post('/inventory/conditions/', input);
    return data;
  },
  update: async (id: number, input: Partial<ConditionInput>): Promise<Condition> => {
    const { data } = await apiBase.patch(`/inventory/conditions/${id}/`, input);
    return data;
  },
  remove: async (id: number): Promise<void> => {
    await apiBase.delete(`/inventory/conditions/${id}/`);
  },
};

/* ───────── Loan Policies ───────── */
export interface LoanPolicy {
  id: number;
  user_type: string;
  max_books: number;
  loan_period_days: number;
  max_renewals: number;
  fine_per_day: string;
  grace_period_days: number;
  created_at: string;
  updated_at: string;
}
export interface LoanPolicyInput {
  user_type: string;
  max_books: number;
  loan_period_days: number;
  max_renewals: number;
  fine_per_day: number;
  grace_period_days: number;
}
export const policiesAPI = {
  list: async (): Promise<PaginatedResponse<LoanPolicy>> => {
    const { data } = await apiBase.get('/loans/policies/');
    return data;
  },
  create: async (input: LoanPolicyInput): Promise<LoanPolicy> => {
    const { data } = await apiBase.post('/loans/policies/', input);
    return data;
  },
  update: async (id: number, input: Partial<LoanPolicyInput>): Promise<LoanPolicy> => {
    const { data } = await apiBase.patch(`/loans/policies/${id}/`, input);
    return data;
  },
  remove: async (id: number): Promise<void> => {
    await apiBase.delete(`/loans/policies/${id}/`);
  },
};

/* ───────── Overdue Notices ───────── */
export type NoticeType = 'first_notice' | 'second_notice' | 'final_notice' | 'reminder';
export type DeliveryStatus = 'pending' | 'sent' | 'delivered' | 'failed' | 'bounced';

export interface OverdueNotice {
  id: number;
  loan: number;
  loan_details: {
    id: number;
    book_title: string;
    borrower_name: string;
    due_date: string;
    days_overdue: number;
    current_fine: string;
    status: string;
  };
  notice_type: NoticeType;
  sent_at: string;
  sent_by: number | null;
  sent_by_name: string | null;
  delivery_status: DeliveryStatus | string;
  email_subject: string;
  email_body: string;
  days_overdue_at_send: number;
  fine_amount_at_send: string;
  delivery_error: string;
  borrower_response: string;
  response_received_at: string | null;
  borrower_name: string;
  borrower_email: string;
  book_title: string;
}
export interface OverdueNoticeFilters {
  loan_id?: number;
  status?: string;
  notice_type?: NoticeType;
  page?: number;
  page_size?: number;
}
export interface SendNoticeInput {
  loan_id: number;
  notice_type?: NoticeType;
  custom_message?: string;
}
export const overdueNoticesAPI = {
  list: async (filters: OverdueNoticeFilters = {}): Promise<PaginatedResponse<OverdueNotice>> => {
    const { data } = await apiBase.get('/loans/overdue-notices/', { params: filters });
    return data;
  },
  sendNotice: async (input: SendNoticeInput) => {
    const { data } = await apiBase.post('/loans/overdue-notices/send_notice/', input);
    return data;
  },
};

/* ───────── Book Reviews (moderation) ───────── */
export interface BookReview {
  id: number;
  book: number;
  book_title: string;
  user: number;
  user_name: string;
  rating: number;
  rating_display: string;
  review_text: string;
  created_at: string;
  updated_at: string;
  is_approved: boolean;
  approved_by: number | null;
  approved_by_name: string | null;
  approved_at: string | null;
}
export interface ReviewFilters {
  book?: number;
  rating?: number;
  is_approved?: boolean;
  page?: number;
  page_size?: number;
}
export const reviewsAPI = {
  list: async (filters: ReviewFilters = {}): Promise<PaginatedResponse<BookReview>> => {
    const { data } = await apiBase.get('/catalog/reviews/', { params: filters });
    return data;
  },
  approve: async (id: number): Promise<BookReview> => {
    const { data } = await apiBase.post(`/catalog/reviews/${id}/approve/`);
    return data;
  },
  unapprove: async (id: number): Promise<BookReview> => {
    const { data } = await apiBase.post(`/catalog/reviews/${id}/unapprove/`);
    return data;
  },
  remove: async (id: number): Promise<void> => {
    await apiBase.delete(`/catalog/reviews/${id}/`);
  },
};
